// src/components/auth/ChildLogin.tsx - FULLY RESPONSIVE VERSION
import React, { useState } from 'react'
import { User, Lock, UserPlus, ArrowRight, HelpCircle, Eye, EyeOff } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useChildAuth } from '../../contexts/ChildAuthContext'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { db } from '../../lib/firebase-config'
import { PinRecovery } from './PinRecovery'
import { PinReset } from './PinReset'

export const ChildLogin: React.FC = () => {
  const [username, setUsername] = useState('')
  const [pin, setPin] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showRecovery, setShowRecovery] = useState(false)
  const [recoveryChildId, setRecoveryChildId] = useState('')
  const [showPinReset, setShowPinReset] = useState(false)
  const { authenticateChild } = useChildAuth()
  const [showPin, setShowPin] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!username.trim()) {
      setError('Please enter your username')
      return
    }
    
    if (pin.length !== 4) {
      setError('Please enter your 4-digit PIN')
      return
    }

    setLoading(true)
    setError('')

    try {
      // Find child by username
      const childrenQuery = query(
        collection(db, 'children'),
        where('username', '==', username.trim())
      )
      const childrenSnapshot = await getDocs(childrenQuery)
      
      if (childrenSnapshot.empty) {
        setError('Username not found. Please check your spelling or sign up.')
        setLoading(false)
        return
      }

      const childDoc = childrenSnapshot.docs[0]
      const childId = childDoc.id
      
      // Authenticate with PIN
      const success = await authenticateChild(childId, pin)
      
      if (!success) {
        setError('Wrong PIN! Please try again.')
        setPin('')
      }
      
    } catch (err: any) {
      console.error('Login error:', err)
      setError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPin = async () => {
    if (!username.trim()) {
      setError('Please enter your username first')
      return
    }

    setLoading(true)
    setError('')

    try {
      // Find child by username
      const childrenQuery = query(
        collection(db, 'children'),
        where('username', '==', username.trim())
      )
      const childrenSnapshot = await getDocs(childrenQuery)
      
      if (childrenSnapshot.empty) {
        setError('Username not found. Please check your spelling.')
        setLoading(false)
        return
      }

      const childDoc = childrenSnapshot.docs[0]
      const childData = childDoc.data()
      
      // Check if child has security questions set up
      if (!childData.securityQuestions || childData.securityQuestions.length === 0) {
        setError('No recovery questions set up. Please ask a grown-up for help.')
        setLoading(false)
        return
      }

      // Show recovery screen
      setRecoveryChildId(childDoc.id)
      setShowRecovery(true)
      
    } catch (err: any) {
      console.error('Recovery error:', err)
      setError('Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleRecoverySuccess = () => {
    // Reset PIN was successful through recovery
    setShowRecovery(false)
    setShowPinReset(true) // Show PIN reset instead of alert
    setPin('')
    setError('')
  }

  const handlePinResetSuccess = () => {
    setShowPinReset(false)
    setRecoveryChildId('')
    setError('')
    // Show success message
    alert('🎉 Your new PIN is ready! You can now log in with your new PIN.')
  }
  
  const handlePinResetCancel = () => {
    setShowPinReset(false)
    setRecoveryChildId('')
    setError('')
  }

  const handleRecoveryCancel = () => {
    setShowRecovery(false)
    setRecoveryChildId('')
    setError('')
  }

  const handlePinChange = (value: string) => {
    // Only allow numbers, max 4 digits
    if (value.length <= 4 && /^\d*$/.test(value)) {
      setPin(value)
      setError('')
    }
  }

  const handleSignUp = () => {
    // Always redirect to parent-signup for new account creation
    console.log('🚀 Redirecting to parent signup from child login')
    window.location.href = '/parent-signup'
  }

  // Show recovery screen if activated
  if (showRecovery && recoveryChildId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 
                      dark:from-gray-900 dark:via-indigo-900/20 dark:to-purple-900/20 
                      flex items-center justify-center p-3 sm:p-4 md:p-6">
        <div className="w-full max-w-sm sm:max-w-md md:max-w-2xl">
          <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-xl overflow-hidden">
            <PinRecovery
              childId={recoveryChildId}
              onSuccess={handleRecoverySuccess}
              onCancel={handleRecoveryCancel}
            />
          </div>
        </div>
      </div>
    )
  }

  // Show PIN reset screen if activated
  if (showPinReset && recoveryChildId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 
                      dark:from-gray-900 dark:via-indigo-900/20 dark:to-purple-900/20 
                      flex items-center justify-center p-3 sm:p-4 md:p-6">
        <div className="w-full max-w-sm sm:max-w-md">
          <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-xl overflow-hidden">
            <PinReset
              childId={recoveryChildId}
              childUsername={username}
              onSuccess={handlePinResetSuccess}
              onCancel={handlePinResetCancel}
            />
          </div>
        </div>
      </div>
    )
  } 

  // Main login screen - FULLY RESPONSIVE
  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-3 sm:p-4 md:p-6 lg:p-8">
      
      {/* Responsive Container */}
      <div className="w-full 
                      max-w-sm sm:max-w-md md:max-w-lg lg:max-w-md xl:max-w-lg
                      mx-auto">
        
        {/* Main Card - Responsive Padding & Radius */}
        <div className="bg-white dark:bg-gray-800 
                        rounded-xl sm:rounded-2xl 
                        shadow-xl 
                        p-4 sm:p-6 md:p-8 lg:p-8
                        mx-auto">
          
          {/* Logo/Brand - Responsive Sizing */}
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl font-semibold text-[#1F2937] mb-2">
              Sign In
            </h1>
            <p className="text-base text-[#6B7280]">
              Enter your username and PIN
            </p>
          </div>

          {/* Login Form - Responsive Spacing */}
          <form onSubmit={handleSubmit} data-form-type="other" className="space-y-4 sm:space-y-5 md:space-y-6"> 
 
            {/* Username Field */}
            <div>
              <label htmlFor="username" 
                     className="block text-sm sm:text-base 
                                font-medium text-gray-700 dark:text-gray-300 
                                mb-2 sm:mb-3
                                px-1">
                Username
              </label>
              <div className="relative">
                {/* Icon - Responsive Size */}
                <User className="absolute left-3 sm:left-4 
                                top-1/2 transform -translate-y-1/2 
                                text-gray-400 
                                w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6
                                pointer-events-none" />
                
                {/* Input - Mobile Optimized */}
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Your username"
                  className="w-full pl-12 pr-4 py-3 border border-[#D1D5DB] rounded-lg bg-white text-[#1F2937]
                            focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981] transition-all duration-200
                            text-base min-h-[44px] font-medium"
                  required
                  autoComplete="off"
                  data-lpignore="true"
                  autoCapitalize="none"
                  autoCorrect="off"
                />
              </div>
            </div>

            {/* PIN Field */}
            <div>
              <label htmlFor="pin" 
                     className="block text-sm sm:text-base 
                                font-medium text-gray-700 dark:text-gray-300 
                                mb-2 sm:mb-3
                                px-1">
                PIN
              </label>
              <div className="relative">
                <Lock className="absolute left-3 sm:left-4 md:left-5 top-1/2 transform -translate-y-1/2 
                                text-gray-400 dark:text-gray-500 
                                w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                <input
                  type={showPin ? "text" : "password"}
                  value={pin}
                  onChange={(e) => handlePinChange(e.target.value)}
                  placeholder="••••"
                  className="w-full pl-12 pr-14 py-3 border border-[#D1D5DB] rounded-lg bg-white text-[#1F2937]
                            focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981] transition-all duration-200
                            text-2xl text-center tracking-widest font-mono min-h-[44px]"
                  maxLength={4}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="new-password"
                  data-lpignore="true"
                  autoCapitalize="none"
                  autoCorrect="off"
                />
                <button
                  type="button"
                  onClick={() => setShowPin(!showPin)}
                  className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 
                            text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {showPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {/* PIN Visual Dots - Responsive */}
              <div className="flex justify-center gap-2 sm:gap-3 md:gap-4 
                              mt-3 sm:mt-4">
                {[0, 1, 2, 3].map((index) => (
                  <div
                    key={index}
                    className={`w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 
                                rounded-full transition-all duration-200 ${
                      index < pin.length 
                        ? 'bg-indigo-600 dark:bg-indigo-400 scale-110' 
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Error Message - Responsive */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 
                            border-2 border-red-200 dark:border-red-800 
                            rounded-lg sm:rounded-xl 
                            p-3 sm:p-4 
                            text-red-700 dark:text-red-400 
                            text-sm sm:text-base
                            text-center
                            font-medium">
                {error}
              </div>
            )}

            {/* Submit Button - Touch Optimized */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#10B981] hover:bg-[#059669] disabled:bg-gray-400 disabled:cursor-not-allowed
                        text-white font-medium py-3 px-6 rounded-lg transition-all duration-200
                        flex items-center justify-center gap-2 text-base min-h-[44px]
                        focus:ring-4 focus:ring-[#10B981]/20 active:scale-95 shadow-lg hover:shadow-xl">
              {loading ? (
                <div className="w-5 h-5 sm:w-6 sm:h-6 
                              border-2 border-white border-t-transparent 
                              rounded-full animate-spin" />
              ) : (
                <>
                  <span className="!text-white">Let's Go!</span>
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 !text-white" />
                </>
              )}
            </Button>
          </form>

          {/* Help Options - Responsive Layout */}
          <div className="mt-6 sm:mt-8 md:mt-10 
                          space-y-4 sm:space-y-5">
            
            {/* Forgot PIN - Touch Friendly */}
            <div className="text-center">
              <button
                onClick={handleForgotPin}
                className="text-[#10B981] hover:text-[#059669] text-sm transition-colors duration-200
                          p-3 -m-3 rounded-lg min-h-[44px] focus:ring-2 focus:ring-[#10B981]/20 active:scale-95">
                Forgot your PIN?
              </button>
            </div>

            {/* Divider - Responsive */}
            <div className="relative flex items-center">
              <div className="flex-grow border-t border-gray-300 dark:border-gray-600"></div>
              <span className="flex-shrink-0 px-3 sm:px-4 
                              text-gray-500 dark:text-gray-400 
                              text-xs sm:text-sm
                              bg-white dark:bg-gray-800">
                or
              </span>
              <div className="flex-grow border-t border-gray-300 dark:border-gray-600"></div>
            </div>

            {/* Sign Up Section - Touch Optimized */}
            <div className="text-center mt-8 pt-6 border-t border-[#E5E7EB]">
              <p className="text-[#6B7280] text-sm mb-3">
                Don't have an account?
              </p>
              <Button
                onClick={handleSignUp}
                variant="outline"
                className="border border-[#D1D5DB] text-[#374151] hover:bg-[#F3F4F6] px-6 py-2 text-sm
                          font-medium min-h-[44px] transition-all duration-200 focus:ring-2 focus:ring-[#10B981]/20">
                <UserPlus className="w-4 h-4 mr-2" />
                <span>Create account</span>
              </Button>
            </div>
          </div>

          {/* Warm footer accent - just above bottom */}
          <div className="mt-8 text-center">
            <div className="inline-block px-4 py-2 bg-[#F0FDF4] rounded-full">
              <p className="text-sm text-[#059669]">Learning made for different brains</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}